// Real product photos for the menus, the same kind the Sale menu uses: phones, tablets and laptops
// from the store's catalog (iPhones from westcoastwirelessca.com, the rest from Swappa), repair
// parts from PHORA's supplier listings (phorascreenparts.com) and consoles from Swappa. Shopify
// photos are fetched at the size they're shown (?width=), so the menus load fast on a tablet.
// A photo that doesn't load falls back to the drawn picture.

const PHORA = 'https://cdn.shopify.com/s/files/1/0702/9751/8128/files/';
const WCW = 'https://cdn.shopify.com/s/files/1/0802/1101/7771/files/';
const SWAPPA = 'https://static.swappa.com/media/product/';

/** Phones, tablets, laptops and consoles used on the menus. */
export const DEVICE_PHOTOS = {
  iphone: `${WCW}iPhone_17_Pro_Max_Cosmic_Orange_540x_867193cc-9934-42e6-8936-489a32342588.webp`,
  iphoneBlack: `${WCW}iPhone_15_Pro_Black_360x_dc9ae1a7-cd07-4175-aaf7-6f512f75ae8c.webp`,
  galaxy: `${SWAPPA}samsung-galaxy-s26-ultra/samsung-galaxy-s26-ultra-06.png`,
  pixel: `${SWAPPA}google-pixel-11-pro/google-pixel-11-pro-01.png`,
  razr: `${SWAPPA}motorola-razr-ultra-2026/motorola-razr-ultra-2026-01.png`,
  ipad: `${SWAPPA}apple-ipad-pro-13-m5-2025/apple-ipad-pro-13-m5-2025-01.png`,
  macbook: `${SWAPPA}macbook-air-2026-m5-15/macbook-air-2026-m5-15-01.png`,
  ps5: `${SWAPPA}playstation-5/playstation-5-01.png`,
  ps4: `${SWAPPA}playstation-4/playstation-4-01.png`,
  xboxOneX: `${SWAPPA}xbox-one-x/xbox-one-x-01.png`,
  switch2: `${SWAPPA}nintendo-switch-2/nintendo-switch-2-01.png`,
  switchOled: `${SWAPPA}nintendo-switch-oled/nintendo-switch-oled-01.png`,
  switch1: `${SWAPPA}nintendo-switch/nintendo-switch-01.png`,
  switchLite: `${SWAPPA}nintendo-switch-lite/nintendo-switch-lite-01.png`,
} as const;

/** Real replacement parts from PHORA's supplier listings. */
export const PART_PHOTOS = {
  screen: `${PHORA}0a06043e807d7eb6e2a59eb5720fadb5.jpg`,
  battery: `${PHORA}16pm-base.jpg`,
  chargingPort: `${PHORA}b57783e0757a21082fb6c1a4de76ed81.jpg`,
  backGlass: `${PHORA}239692174c9cd46e1615d9f3f63ac0c8.jpg`,
  camera: `${PHORA}10af1b5a44c13db03ba8bcc2d52fe111.jpg`,
  speaker: `${PHORA}99023d894629a2e5ad2896059eb5f11c.jpg`,
  ipadScreen: `${PHORA}ipair5b-blk-1-1-1-1.jpg`,
  macbookScreen: `${PHORA}pr-a3403-blk-1-1-1.jpg`,
  consolePort: `${PHORA}ps5-hdmi-pr.jpg`,
  simTray: `${PHORA}16pm-sst-blk-1.jpg`,
  tools: `${PHORA}bst-9909.jpg`,
  toolsKit: `${PHORA}2uul-scrooset.jpg`,
} as const;

/** The home screen's buttons. */
export const TASK_PHOTOS: Record<string, string> = {
  sale: DEVICE_PHOTOS.iphone,
  repair: PART_PHOTOS.screen,
  buy: DEVICE_PHOTOS.galaxy,
  trade: DEVICE_PHOTOS.pixel,
  activation: PART_PHOTOS.simTray,
  billpay: DEVICE_PHOTOS.razr,
  unlock: DEVICE_PHOTOS.iphoneBlack,
  order: PART_PHOTOS.chargingPort,
};

/** The repair check-in's "What are we fixing?" tiles. */
export const REPAIR_TYPE_PHOTOS: Record<string, string> = {
  iphone: DEVICE_PHOTOS.iphone,
  phone: DEVICE_PHOTOS.galaxy,
  ipad: DEVICE_PHOTOS.ipad,
  macbook: DEVICE_PHOTOS.macbook,
  console: DEVICE_PHOTOS.ps5,
  desktop: PART_PHOTOS.tools,
  car: PART_PHOTOS.toolsKit,
};

/** The part a repair replaces, by its name ("Screen replacement", "Battery", "HDMI port"...). */
export function repairPhotoFor(text: string, deviceType?: string | null): string {
  const t = text.toLowerCase();
  if (/hdmi|disc drive|fan\b|controller/.test(t)) return PART_PHOTOS.consolePort;
  if (/back ?glass|back housing|housing|rear glass/.test(t)) return PART_PHOTOS.backGlass;
  if (/battery/.test(t)) return PART_PHOTOS.battery;
  if (/charg|port|usb|lightning|dock/.test(t)) return deviceType === 'console' ? PART_PHOTOS.consolePort : PART_PHOTOS.chargingPort;
  if (/camera|lens/.test(t)) return PART_PHOTOS.camera;
  if (/speaker|earpiece|microphone|\bmic\b|audio/.test(t)) return PART_PHOTOS.speaker;
  if (/sim/.test(t)) return PART_PHOTOS.simTray;
  if (/screen|lcd|oled|display|digitizer|glass/.test(t)) {
    if (deviceType === 'ipad' || /ipad|tablet|\btab\b/.test(t)) return PART_PHOTOS.ipadScreen;
    if (deviceType === 'macbook' || /macbook|laptop/.test(t)) return PART_PHOTOS.macbookScreen;
    return PART_PHOTOS.screen;
  }
  if (deviceType === 'console') return PART_PHOTOS.consolePort;
  return PART_PHOTOS.tools;
}

/** A photo at about the size it's shown: Shopify resizes on the fly; other hosts are used as they are. */
export function sizedPhoto(url: string | null | undefined, px: number): string | null {
  if (!url) return null;
  if (!url.startsWith('https://cdn.shopify.com/')) return url;
  const w = Math.min(800, Math.max(100, Math.ceil((px * 2) / 50) * 50));
  try {
    const u = new URL(url);
    u.searchParams.set('width', String(w));
    return u.toString();
  } catch {
    return url;
  }
}

// ---------------------------------------------------------------- a model's own photo

/** The words of a model name, for matching photos: "Galaxy S26+" and "Galaxy S26 Plus" match. */
export function photoWords(model: string): string[] {
  return model
    .toLowerCase()
    .replace(/\bps(\d)\b/g, 'playstation $1')
    .replace(/["”]/g, ' ')
    .replace(/\b(apple|samsung|google|motorola|sony|microsoft|nintendo)\b/g, ' ')
    .replace(/\b(5g|lte|4g|unlocked|console)\b/g, ' ')
    .replace(/\(?\b(19|20)\d{2}\b\)?/g, ' ')
    .replace(/\+/g, ' plus ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
}

export const photoKey = (model: string) => photoWords(model).join('');

/**
 * A model's photo from the index (catalog models, newest first): the same model, or failing
 * that the closest one of its family ("iPad Pro 11 (4th Gen)" finds an iPad Pro 11). Never a
 * different family: no photo is better than the wrong phone.
 */
export function matchModelPhoto(index: [string, string][], model: string | null | undefined): string | null {
  const words = photoWords(model ?? '');
  if (!words.length) return null;
  const full = words.join('');
  const exact = index.find(([k]) => k === full);
  if (exact) return exact[1];
  for (let n = words.length; n >= Math.min(2, words.length); n--) {
    const pre = words.slice(0, n).join('');
    if (pre.length < 4) break;
    const hit = index.find(([k]) => k.startsWith(pre));
    if (hit) return hit[1];
  }
  return null;
}

// ---------------------------------------------------------------- a product's picture from its name

/** A catalog item and its picture (catalog/catalog.json). */
export interface CatalogPhoto { name: string; cat: string; img: string | null }

/**
 * Kinds of accessories by the words people type ("Charger Apple Type C" is a charger), and where
 * the catalog keeps each. The first kind that fits wins: a car charger is a car accessory, a power
 * bank isn't a charger, an AUX cable is an audio adapter. `model`: it's for one phone (a case, a
 * screen protector), so only one for the same phone will do. `fallback`: what to show when nothing
 * closer is listed (null: nothing; '': the first one of its kind).
 */
const ACCESSORY_KINDS: { cats: string[]; model: boolean; fallback: string | null; re: RegExp; unless?: RegExp }[] = [
  { cats: ['cases'], model: true, fallback: null, re: /\b(case|cover|folio|bumper)\b/ },
  { cats: ['screen_protectors'], model: true, fallback: null, re: /\b(screen ?protector|protector|tempered|privacy|glass)\b/, unless: /\b(back|rear) glass\b/ },
  { cats: ['car_accessories'], model: false, fallback: 'car charger usbc', re: /\b(car|vent|dashboard)\b/ },
  { cats: ['power_banks'], model: false, fallback: 'power bank 10000mah', re: /\b(power ?bank|battery pack|portable charger)\b/ },
  { cats: ['adapters'], model: false, fallback: 'aux cable', re: /\b(aux|auxiliary|audio adapter|headphone (adapter|jack)|3 5 ?mm|dongle)\b/ },
  { cats: ['other_accessories'], model: false, fallback: 'memory card', re: /\b(sd ?card|memory card|micro ?sd)\b/ },
  { cats: ['chargers'], model: false, fallback: 'charger usbc white', re: /\b(charger|charging (block|brick|pad|stand)|adapter|plug|brick|block|wireless charg\w*|magsafe)\b/ },
  { cats: ['cables'], model: false, fallback: 'usbc cable', re: /\b(cable|cord|wire)\b/ },
  { cats: ['headphones'], model: false, fallback: 'wireless earbuds', re: /\b(earbuds?|airpods?|earphones?|headphones?|headset|handsfree|buds)\b/ },
  { cats: ['speakers'], model: false, fallback: 'portable speaker', re: /\b(speaker|soundbar|boombox)\b/ },
  { cats: ['sunglasses'], model: false, fallback: '', re: /\b(sunglasses|shades)\b/ },
];
const PRODUCT_STOP = new Set(['for', 'with', 'and', 'the', 'a', 'an', 'of', 'to', 'in', 'by', 'new', 'fast', 'set', 'pieces', 'piece', 'bulk', 'pack']);
const BRAND_WORDS = new Set(['apple', 'samsung', 'google', 'galaxy', 'pixel', 'mello', 'atalax', 'inventuv', 'surgemax', 'anker', 'belkin', 'otterbox', 'mophie']);
const NOT_APPLE = ['samsung', 'galaxy', 'google', 'pixel', 'android', 'moto', 'motorola'];
const SPEC_WORDS = new Set(['usbc', 'usba', 'lightning', 'wireless', 'magnetic', 'magsafe', 'privacy', 'clear']);
const MODEL_WORDS = new Set(['pro', 'max', 'plus', 'mini', 'ultra', 'fe', 'lite', 'e']);
const COLOR_WORDS = new Set(['white', 'black', 'gray', 'grey', 'silver', 'blue', 'red', 'pink', 'green', 'purple', 'gold']);

/** A product name as plain words, the usual spellings made the same: "Type-C", "USB C" and "USB-C" are usbc, "30 Watts" is 30w. */
function productText(name: string): string {
  return ` ${name.toLowerCase()} `
    .replace(/\busb[\s-]*type[\s-]*c\b/g, ' usbc ')
    .replace(/\btype[\s-]*c\b/g, ' usbc ')
    .replace(/\busb[\s-]*c\b/g, ' usbc ')
    .replace(/\bc to c\b/g, ' usbc to usbc ')
    .replace(/\busb[\s-]*a\b/g, ' usba ')
    .replace(/\blight[nh]ing\b/g, ' lightning ')
    .replace(/\bmagsafe\b/g, ' magsafe magnetic ')
    .replace(/\b(\d+)\s*(w|watts?)\b/g, ' $1w ')
    .replace(/\b(\d+)\s*(ft|feet|foot)\b/g, ' $1ft ')
    .replace(/\b(\d+)\s*mah\b/g, ' $1mah ')
    .replace(/\+/g, ' plus ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const textWords = (text: string): string[] => text.split(' ').filter((w) => w && !PRODUCT_STOP.has(w));

/** A product name's words for matching ("Charger Apple Type C" → charger, apple, usbc). */
export const productWords = (name: string): string[] => textWords(productText(name));

/** The catalog item of `cats` closest to `words` (the fewest extra words on a tie), or null when none fits. */
function closestItem(items: CatalogPhoto[], words: string[], cats: string[], model: boolean): CatalogPhoto | null {
  const want = new Set(words);
  // A case or a screen protector is for one phone: its numbers ("13", "s24") must all be there.
  const numbers = model ? words.filter((w) => /^[a-z]?\d{1,2}$/.test(w)) : [];
  const notApple = NOT_APPLE.some((w) => want.has(w));
  const color = words.some((w) => COLOR_WORDS.has(w));
  let best: { item: CatalogPhoto; score: number; size: number } | null = null;
  for (const item of items) {
    if (!item.img || !cats.includes(item.cat)) continue;
    const has = productWords(item.name);
    const set = new Set(has);
    if (numbers.some((n) => !set.has(n))) continue;
    let score = 0;
    for (const w of want) if (set.has(w)) score += BRAND_WORDS.has(w) ? 0.5 : SPEC_WORDS.has(w) ? 2 : 1;
    // A case for the iPhone 15 isn't one for the 15 Pro Max.
    if (model) for (const w of set) if (MODEL_WORDS.has(w) && !want.has(w)) score -= 1;
    // Not a Lightning one for a USB-C (or Samsung) charger, not a wireless pad for a wall charger.
    if (set.has('lightning') && !want.has('lightning') && (want.has('usbc') || notApple)) score -= 2;
    for (const w of ['wireless', 'magnetic']) if (set.has(w) && !want.has(w)) score -= 0.5;
    // Apple's own chargers and cables are white.
    if (!color && (want.has('apple') || want.has('iphone')) && set.has('white')) score += 0.5;
    if (!best || score > best.score || (score === best.score && has.length < best.size)) best = { item, score, size: has.length };
  }
  return best && best.score > 0 ? best.item : null;
}

/**
 * A picture for a product from the built-in catalog, by its name: the closest accessory of the
 * same kind ("Charger Apple Type C" → a white USB-C wall charger, "iPhone 13 case" → a case for
 * the iPhone 13). A case or screen protector only for the same phone; any other accessory gets
 * one of its kind when nothing closer is listed. `categoryId` (the category picked) counts when
 * the name doesn't say what it is ("Mello X16" in Chargers). Null when it isn't an accessory, or
 * no case or protector fits: the drawn picture shows then.
 */
export function matchProductPhoto(items: CatalogPhoto[], name: string, categoryId?: string | null): { img: string; name: string } | null {
  const text = productText(name);
  // Cases is where the Add product form starts, so it doesn't say anything by itself.
  const kind = ACCESSORY_KINDS.find((k) => k.re.test(text) && !k.unless?.test(text))
    ?? (categoryId && categoryId !== 'cases' ? ACCESSORY_KINDS.find((k) => k.cats.includes(categoryId)) : undefined);
  if (!kind) return null;
  let hit = closestItem(items, textWords(text), kind.cats, kind.model);
  if (!hit && kind.fallback !== null) {
    hit = (kind.fallback ? closestItem(items, productWords(kind.fallback), kind.cats, false) : null) ?? items.find((it) => !!it.img && kind.cats.includes(it.cat)) ?? null;
  }
  return hit?.img ? { img: hit.img, name: hit.name } : null;
}
